import type { ChatMessage, PatientSwitchConfirmPayload } from '../types/chat'
import { chatContentTrim } from './chatContent'
import { isFrenchLanguage } from './chatUiLocale'

const PATIENT_SWITCH_CONTEXT_RE = /^patient_(switch|open)/

/** Host / voice slot label like ``patient 2`` (not a registry display name). */
export function looksLikePatientChartSlotLabel(name: string): boolean {
  return /^\s*patient\s+\d+\s*$/i.test((name || '').trim())
}

/** Match server copy when metadata was not hydrated on the client. */
export function inferPatientSwitchConfirmFromContent(
  content: unknown,
): PatientSwitchConfirmPayload | undefined {
  const t = chatContentTrim(content)
  if (!t) return undefined

  const enPreferred = t.match(
    /^active patient is\s+(.+?)\.\s*do you want to open\s+(.+?)\??\s*$/i,
  )
  if (enPreferred) {
    return {
      activePatientName: enPreferred[1].trim(),
      requestedPatientName: enPreferred[2].trim(),
      documentLabel: 'patient record',
      context: 'patient_switch_modality',
    }
  }

  const en = t.match(
    /current active patient is\s+(.+?)\.\s*do you want to open\s+(.+?)\??\s*$/i,
  )
  if (en) {
    return {
      activePatientName: en[1].trim(),
      requestedPatientName: en[2].trim(),
      documentLabel: 'patient record',
      context: 'patient_switch_modality',
    }
  }

  const frPreferred = t.match(
    /^le patient actif est\s+(.+?)\.\s*voulez-vous ouvrir\s+(.+?)\??\s*$/i,
  )
  if (frPreferred) {
    return {
      activePatientName: frPreferred[1].trim(),
      requestedPatientName: frPreferred[2].trim(),
      documentLabel: 'dossier patient',
      context: 'patient_switch_modality',
    }
  }

  const fr = t.match(
    /patient actuellement actif est\s+(.+?)\.\s*voulez-vous que j.?ouvre le dossier de\s+(.+?)\??\s*$/i,
  )
  if (fr) {
    return {
      activePatientName: fr[1].trim(),
      requestedPatientName: fr[2].trim(),
      documentLabel: 'dossier patient',
      context: 'patient_switch_modality',
    }
  }

  const enSports = t.match(
    /active patient in context is\s+(.+?),?\s*but you asked.*for\s+(.+?)\.\s*do you want me to open\s+/i,
  )
  if (enSports) {
    return {
      activePatientName: enSports[1].trim(),
      requestedPatientName: enSports[2].trim(),
      documentLabel: 'sports no-objection certificate',
      context: 'patient_switch_sports_noc',
    }
  }

  return undefined
}

export function parsePatientSwitchConfirmFromMetadata(
  md: Record<string, unknown> | undefined,
): PatientSwitchConfirmPayload | undefined {
  const raw = md?.patient_switch_confirm ?? md?.patientSwitchConfirm
  if (!raw || typeof raw !== 'object') return undefined
  const o = raw as Record<string, unknown>
  const activePatientName = String(o.active_patient_name ?? o.activePatientName ?? '').trim()
  const requestedPatientName = String(
    o.requested_patient_name ?? o.requestedPatientName ?? md?.pending_patient ?? '',
  ).trim()
  const documentLabel = String(o.document_label ?? o.documentLabel ?? o.modality ?? '').trim()
  if (!requestedPatientName) return undefined
  return {
    activePatientName: activePatientName || 'the current patient',
    requestedPatientName,
    documentLabel: documentLabel || 'patient record',
    modality: o.modality != null ? String(o.modality) : undefined,
    recency: o.recency != null ? String(o.recency) : undefined,
    context: o.context != null ? String(o.context) : undefined,
  }
}

/** Assistant row asking to open/switch patient before continuing. */
export function isPatientSwitchConfirmationMessage(m: ChatMessage): boolean {
  if (m.patientSwitchConfirm) return true
  if (inferPatientSwitchConfirmFromContent(m.content)) return true
  if (!m.awaitingGeneralConfirmation) return false
  const ctx = (m.generalConfirmationContext || '').trim().toLowerCase()
  return PATIENT_SWITCH_CONTEXT_RE.test(ctx)
}

export function patientSwitchConfirmIsActionable(m: ChatMessage, hist: boolean): boolean {
  if (hist || m.patientSwitchConfirmResolved) return false
  if (m.patientSwitchConfirm || inferPatientSwitchConfirmFromContent(m.content)) return true
  return isPatientSwitchConfirmationMessage(m) && m.awaitingGeneralConfirmation === true
}

/** Ensure switch payload exists for yes/no handlers when only message text was persisted. */
export function enrichPatientSwitchConfirmOnMessage(m: ChatMessage): ChatMessage {
  if (m.patientSwitchConfirm || m.role !== 'assistant') return m
  const inferred = inferPatientSwitchConfirmFromContent(m.content)
  if (!inferred) return m
  return {
    ...m,
    patientSwitchConfirm: inferred,
    awaitingGeneralConfirmation: m.awaitingGeneralConfirmation ?? true,
    generalConfirmationContext:
      m.generalConfirmationContext || inferred.context || 'patient_switch_modality',
  }
}

/** User already sent yes/no after this patient-switch prompt. */
export function hasPatientSwitchUserAnswerAfter(
  messages: readonly ChatMessage[],
  assistantIndex: number,
): boolean {
  for (let j = assistantIndex + 1; j < messages.length; j++) {
    const m = messages[j]
    if (m?.role !== 'user') continue
    if (userMessageIsPatientSwitchYes(m) || userMessageIsPatientSwitchNo(m)) return true
  }
  return false
}

/** Latest assistant row waiting for patient-switch yes/no (inline card or voice). */
export function findPendingPatientSwitchConfirm(
  messages: readonly ChatMessage[],
): { messageId: string } | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || m.historySnapshot) continue
    if (hasPatientSwitchUserAnswerAfter(messages, i)) continue
    if (patientSwitchConfirmIsActionable(m, false)) return { messageId: m.id }
  }
  return null
}

/** Reminder preview waiting for yes/no — uses same ``yes proceed`` / ``no cancel`` strings as general confirm. */
export function hasPendingRemindConfirmation(messages: readonly ChatMessage[]): boolean {
  for (let i = messages.length - 1; i >= 0; i--) {
    const m = messages[i]
    if (m.role !== 'assistant' || m.historySnapshot) continue
    if (m.awaitingRemindConfirmation === true && !m.reminderPreviewResolved) return true
  }
  return false
}

/** After a successful switch confirm, disable all outstanding switch cards. */
export function markAllPatientSwitchConfirmationsResolved(
  messages: readonly ChatMessage[],
): ChatMessage[] {
  return messages.map((m) =>
    isPatientSwitchConfirmationMessage(m) && !m.patientSwitchConfirmResolved
      ? {
          ...m,
          patientSwitchConfirmResolved: true,
          awaitingGeneralConfirmation: false,
        }
      : m,
  )
}

/** User already answered patient-switch yes/no (do not treat as a new command). */
export function userTextIsPatientSwitchConfirmFollowUp(text: string): boolean {
  return parsePatientSwitchVoiceAnswer(text) !== null
}

/** Normalize labels like ``Yes, proceed`` / ``yes proceed`` for matching. */
export function normalizePatientSwitchAnswerText(text: unknown): string {
  return chatContentTrim(text)
    .toLowerCase()
    .replace(/[,!?.'"]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim()
}

export function userMessageIsPatientSwitchYes(m: ChatMessage): boolean {
  if (m.role !== 'user') return false
  if (parsePatientSwitchVoiceAnswer(m.content) === 'yes') return true
  const n = normalizePatientSwitchAnswerText(m.content)
  return (
    n === 'yes' ||
    n === 'yes proceed' ||
    n === 'yes continue' ||
    n === 'yes open' ||
    n === 'oui' ||
    n === 'oui continuer' ||
    n === 'oui ouvrir'
  )
}

export function userMessageIsPatientSwitchNo(m: ChatMessage): boolean {
  if (m.role !== 'user') return false
  if (parsePatientSwitchVoiceAnswer(m.content) === 'no') return true
  const n = normalizePatientSwitchAnswerText(m.content)
  return n === 'no' || n === 'no cancel' || n === 'non' || n === 'non annuler'
}

export function countPatientSwitchYesUserMessages(messages: readonly ChatMessage[]): number {
  let n = 0
  for (const m of messages) {
    if (userMessageIsPatientSwitchYes(m)) n++
  }
  return n
}

/** Keep a single yes-proceed user bubble (stops runaway UI loops). */
export function dedupePatientSwitchYesUserMessages(messages: readonly ChatMessage[]): ChatMessage[] {
  let keptYes = false
  const out: ChatMessage[] = []
  for (const m of messages) {
    if (userMessageIsPatientSwitchYes(m)) {
      if (keptYes) continue
      keptYes = true
    }
    out.push(m)
  }
  return out
}

export function chatHasPatientSwitchYesReply(messages: readonly ChatMessage[]): boolean {
  return countPatientSwitchYesUserMessages(messages) > 0
}

/** Bubble text shown after patient-switch Yes (matches inline card, not general bar). */
export function patientSwitchConfirmBubbleText(choice: 'yes' | 'no'): string {
  const fr = isFrenchLanguage()
  if (choice === 'yes') return fr ? 'Oui' : 'Yes'
  return fr ? 'Non' : 'No'
}

const PATIENT_SWITCH_USER_COMMITTED_KEY = 'voicnav:patientSwitchUserCommitted'

export function markPatientSwitchUserCommitted(): void {
  try {
    sessionStorage.setItem(PATIENT_SWITCH_USER_COMMITTED_KEY, '1')
  } catch {
    /* ignore */
  }
}

export function isPatientSwitchUserCommitted(): boolean {
  try {
    return sessionStorage.getItem(PATIENT_SWITCH_USER_COMMITTED_KEY) === '1'
  } catch {
    return false
  }
}

export function clearPatientSwitchUserCommitted(): void {
  try {
    sessionStorage.removeItem(PATIENT_SWITCH_USER_COMMITTED_KEY)
  } catch {
    /* ignore */
  }
}

/** Block general yes/no bar from re-firing after patient-switch yes/no UI. */
export function chatHasPatientSwitchConfirmFollowUp(messages: readonly ChatMessage[]): boolean {
  for (const m of messages) {
    if (userMessageIsPatientSwitchYes(m) || userMessageIsPatientSwitchNo(m)) return true
  }
  return false
}

/** Normalize patient labels for header/session comparison (not strict identity). */
export function patientNamesLikelyEqual(a: string, b: string): boolean {
  const x = (a || '').trim().toLowerCase()
  const y = (b || '').trim().toLowerCase()
  if (!x || !y) return false
  return x === y
}

/** Voice/typed short answers for patient switch (matches widget ``yes proceed`` / ``no cancel``). */
export function parsePatientSwitchVoiceAnswer(text: string): 'yes' | 'no' | null {
  const t = normalizePatientSwitchAnswerText(text)
  if (!t) return null
  if (
    /\b(send\s+the\s+mail|cancel\s+mail|envoyer\s+le\s+courriel|annuler\s+le\s+courriel)\b/i.test(
      t,
    )
  ) {
    return null
  }
  const compact = t.replace(/\s+/g, '')
  if (
    compact === 'yesproceed' ||
    compact === 'yescontinue' ||
    compact === 'yesopen' ||
    t === 'yes' ||
    t.startsWith('yes ') ||
    t === 'oui' ||
    t.startsWith('oui ')
  ) {
    return 'yes'
  }
  if (
    compact === 'nocancel' ||
    t === 'no' ||
    t.startsWith('no ') ||
    t === 'non' ||
    t.startsWith('non ')
  ) {
    return 'no'
  }
  return null
}
